// client/constants/pagination.js
/**
 * 🏢 Enterprise: Zentrale Pagination-Konfiguration für WireTrack Frontend
 * 
 * Diese Konstanten sollten für alle Listen, Tabellen und API-Anfragen verwendet werden.
 * Änderungen hier wirken sich auf die gesamte App aus.
 * 
 * ⚠️ WICHTIG: Diese Werte müssen mit den Backend-Limits in `utils/constants.js` übereinstimmen!
 */

/** Standard-Seitengröße für Listen (Kunden, Aufträge, Projekte, etc.) */
export const DEFAULT_PAGE_SIZE = 100;

/** Maximale Seitengröße (Client kann nicht mehr anfordern, wird serverseitig geprüft) */
export const MAX_PAGE_SIZE = 7500;

/** Kundenliste: erlaubte Seitengrössen + Server-Clamp (mit Backend konsistent halten) */
export const KUNDEN_PAGE_SIZE_OPTIONS = Object.freeze([50, 100, 250]);
export const KUNDEN_LIST_MAX_PAGE_SIZE = 250;

/** Minimale Seitengröße */
export const MIN_PAGE_SIZE = 1;

/** Spezifische Limits für besondere Anwendungsfälle */
export const PAGE_SIZE_LIMITS = Object.freeze({
  /** Legacy (nicht für Kunden-Dropdowns) */
  SELECT_DROPDOWN: 500,
  /** Für Material-Vorschläge (große Datenmenge) */
  MATERIAL_SUGGESTIONS: 10000,
  /** Für Dashboard-Widgets (kompakte Ansicht) */
  WIDGET_COMPACT: 20,
  /** Für Dashboard-Widgets (normale Ansicht) */
  WIDGET_NORMAL: 50,
  /** Für Audit-Logs (begrenzt für Performance) */
  AUDIT_LOG: 100,
  /** Für Benachrichtigungen */
  NOTIFICATIONS: 100,
  /** Für Kalender-Ansichten */
  CALENDAR: 1000,
});

/**
 * Erstellt Pagination-Parameter für API-Anfragen
 * @param {Object} options - Optionen
 * @param {number} [options.page=1] - Aktuelle Seite
 * @param {number} [options.pageSize=DEFAULT_PAGE_SIZE] - Einträge pro Seite
 * @returns {Object} Objekt mit page, pageSize für API-Anfragen
 */
export function getPaginationParams(options = {}) {
  const page = Math.max(1, options.page || 1);
  const pageSize = Math.max(
    MIN_PAGE_SIZE,
    Math.min(MAX_PAGE_SIZE, options.pageSize || DEFAULT_PAGE_SIZE)
  );
  return { page, pageSize };
}

/**
 * Erstellt Query-String für Pagination
 * @param {Object} options - Optionen (page, pageSize)
 * @returns {string} Query-String (z.B. "page=1&pageSize=500")
 */
export function buildPaginationQuery(options = {}) {
  const { page, pageSize } = getPaginationParams(options);
  return `page=${page}&pageSize=${pageSize}`;
}

// Standard-Exporte für einfache Verwendung
export default {
  DEFAULT_PAGE_SIZE,
  MAX_PAGE_SIZE,
  MIN_PAGE_SIZE,
  PAGE_SIZE_LIMITS,
  getPaginationParams,
  buildPaginationQuery,
};
